import { getFunctions, httpsCallable } from 'firebase/functions'

const functions = getFunctions(undefined, 'australia-southeast1')

/**
 * Call the Cloud Function to create a Stripe Checkout session,
 * then redirect the user to the Stripe-hosted payment page.
 */
export async function redirectToCheckout(tier: 'storefront' | 'dealership'): Promise<void> {
  try {
    const createCheckout = httpsCallable<{ tier: string }, { url: string }>(
      functions,
      'stripeCreateCheckout',
    )

    const result = await createCheckout({ tier })
    window.location.href = result.data.url
  } catch (error) {
    console.error('Checkout failed:', error)
    throw new Error('Failed to start checkout. Please try again.')
  }
}

/**
 * Call the Cloud Function to create a Stripe Customer Portal session,
 * then redirect the user to manage their subscription.
 */
/**
 * Call the Cloud Function to create a Stripe Checkout session for a product order.
 * Each call handles one seller's items. Returns the checkout URL and order ID.
 */
export async function createOrderCheckout(data: {
  items: Array<{ listingId: string; quantity: number }>
  shippingOption: 'pickup' | 'courier' | 'post'
  shippingAddress?: {
    name: string
    line1: string
    line2?: string
    city: string
    region: string
    postcode: string
  }
}): Promise<{ url: string; orderId: string }> {
  try {
    const callFn = httpsCallable<typeof data, { url: string; orderId: string }>(
      functions,
      'stripeCreateOrderCheckout',
    )
    const result = await callFn(data)
    return result.data
  } catch (error) {
    console.error('Order checkout failed:', error)
    throw new Error('Failed to start order checkout. Please try again.')
  }
}

/**
 * Call the Cloud Function to create a Stripe Checkout session for a fundraiser purchase.
 * Supports products, raffle tickets, and sponsorship tiers.
 */
export async function createFundraiserCheckout(data: {
  listingId: string
  items: Array<{
    type: 'product' | 'raffle' | 'sponsorship'
    itemId: string
    quantity: number
  }>
}): Promise<{ url: string; orderId: string }> {
  try {
    const callFn = httpsCallable<typeof data, { url: string; orderId: string }>(
      functions,
      'stripeCreateFundraiserCheckout',
    )
    const result = await callFn(data)
    return result.data
  } catch (error: any) {
    console.error('Fundraiser checkout failed:', error)
    // Pass through the server error message (e.g. "seller has not set up payment processing")
    const serverMessage = error?.code === 'functions/failed-precondition'
      ? error.message
      : null
    throw new Error(serverMessage || 'Failed to start checkout. Please try again.')
  }
}

export async function redirectToPortal(): Promise<void> {
  try {
    const createPortal = httpsCallable<Record<string, never>, { url: string }>(
      functions,
      'stripeCreatePortal',
    )

    const result = await createPortal({})
    window.location.href = result.data.url
  } catch (error) {
    console.error('Customer portal failed:', error)
    throw new Error('Failed to open subscription management. Please try again.')
  }
}

/**
 * Call the Cloud Function to create a Stripe Connect Express account,
 * then redirect to Stripe's onboarding flow.
 */
export async function redirectToConnectOnboarding(): Promise<void> {
  try {
    const createConnect = httpsCallable<Record<string, never>, { url: string }>(
      functions,
      'stripeCreateConnectAccount',
    )
    const result = await createConnect({})
    window.location.href = result.data.url
  } catch (error: any) {
    console.error('Stripe Connect onboarding failed:', error)
    const serverMessage =
      error?.code === 'functions/failed-precondition' ? error.message : null
    throw new Error(serverMessage || 'Failed to start seller onboarding. Please try again.')
  }
}

/**
 * Call the Cloud Function to sync the seller's Stripe Connect status from
 * Stripe to Firestore. Used as a self-heal after onboarding to avoid waiting
 * on the webhook.
 */
export async function syncConnectStatus(): Promise<{ status: 'pending' | 'active' | 'restricted' }> {
  const callFn = httpsCallable<Record<string, never>, { status: 'pending' | 'active' | 'restricted' }>(
    functions,
    'stripeSyncConnectStatus',
  )
  const result = await callFn({})
  return result.data
}

/**
 * Call the Cloud Function to get the Stripe Connect dashboard link,
 * then redirect the seller to manage their payouts.
 */
export async function redirectToConnectDashboard(): Promise<void> {
  try {
    const getDashboard = httpsCallable<Record<string, never>, { url: string }>(
      functions,
      'stripeGetConnectDashboard',
    )
    const result = await getDashboard({})
    window.location.href = result.data.url
  } catch (error) {
    console.error('Stripe Connect dashboard failed:', error)
    throw new Error('Failed to open payout dashboard. Please try again.')
  }
}
